import { logInfo, logError } from './logger';
import { generateConfig } from './api';

class AIService {
  async makeRequest(endpoint, data, token) {
    const requestStartTime = Date.now();
    try {
      logInfo('Making AI service request via backend proxy', {
        endpoint,
        dataLength: JSON.stringify(data).length,
        requestStartTime: new Date(requestStartTime).toISOString()
      });

      // Use the existing API configuration utility
      const { baseURL, config } = generateConfig({ token });

      // Create an AbortController for timeout management
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minutes timeout

      try {
        logInfo('Starting frontend fetch to backend AI proxy', {
          url: `${baseURL}/api/ai${endpoint}`,
          startTime: new Date().toISOString()
        });

        const response = await fetch(`${baseURL}/api/ai${endpoint}`, {
          method: 'POST',
          headers: {
            ...config.headers,
          },
          body: JSON.stringify(data),
          signal: controller.signal,
        });

        const fetchEndTime = Date.now();
        const fetchDuration = fetchEndTime - requestStartTime;

        clearTimeout(timeoutId);

        logInfo('Frontend fetch completed', {
          fetchDurationMs: fetchDuration,
          fetchDurationSeconds: Math.round(fetchDuration / 1000),
          responseStatus: response.status,
          responseOk: response.ok
        });

        if (!response.ok) {
          const errorText = await response.text();
          logError('Backend returned non-OK response', {
            status: response.status,
            errorText: errorText,
            fetchDurationSeconds: Math.round(fetchDuration / 1000)
          });
          throw new Error(`AI Service Error: ${response.status} - ${errorText}`);
        }

        const parseStartTime = Date.now();
        const result = await response.json();
        const parseEndTime = Date.now();
        const parseDuration = parseEndTime - parseStartTime;
        const totalDuration = parseEndTime - requestStartTime;

        logInfo('AI service request successful', {
          endpoint,
          responseLength: JSON.stringify(result).length,
          parseDurationMs: parseDuration,
          totalDurationMs: totalDuration,
          totalDurationSeconds: Math.round(totalDuration / 1000)
        });
        return result;
      } catch (error) {
        const errorTime = Date.now();
        const elapsedMs = errorTime - requestStartTime;
        clearTimeout(timeoutId);

        logError('Frontend AI request failed', {
          errorName: error.name,
          errorMessage: error.message,
          elapsedMs: elapsedMs,
          elapsedSeconds: Math.round(elapsedMs / 1000),
          isAbortError: error.name === 'AbortError'
        });

        if (error.name === 'AbortError') {
          throw new Error(`AI processing timed out after ${Math.round(elapsedMs / 1000)} seconds. The model may be processing a large request.`);
        }
        throw error;
      }
    } catch (error) {
      logError('AI service request failed', {
        endpoint,
        error: error.message,
        stack: error.stack
      });

      // Provide more user-friendly error messages
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        throw new Error('Unable to connect to AI service. Please check your internet connection or contact support.');
      } else if (error.message.includes('CORS')) {
        throw new Error('Connection blocked by browser security. Please contact your system administrator.');
      } else if (error.message.includes('504') || error.message.includes('Gateway Time-out')) {
        throw new Error('Request timed out at the server level (likely due to nginx/proxy timeout). The AI server may have processed your request successfully, but the response was cut off during transmission. This is a known issue with large AI responses. Please try again, and if the issue persists, contact support.');
      } else if (error.message.includes('timed out after')) {
        throw new Error('Request processing took longer than expected. The AI server may have completed processing, but the response was interrupted. Please try again or contact support if the issue persists.');
      }

      throw error;
    }
  }

  async summarizeSOAPNotes(soapNotes, clientName, token) {
    try {
      // Format SOAP notes for AI processing
      const formattedNotes = this.formatSOAPNotesForAI(soapNotes, clientName);

      const data = {
        message: formattedNotes,
        system_prompt: `You are an expert Speech-Language Pathologist (SLP) clinical documentation assistant.

        Your task is to review the provided array of SOAP notes for a single client and generate a professional, concise summary suitable for inclusion in clinical progress reports.
        
        **Instructions:**
        
        1. **Only include information that is explicitly documented in the notes.**
           - Do not infer, guess, or fabricate any client demographics (e.g., age, gender) or diagnoses (e.g., autism).
           - If any information is missing or unclear, state clearly that it is not documented.
        
        2. **Identify and summarize key clinical observations and trends over time**, and for each observation or skill area, **clearly indicate whether it relates to Speech and Language or Literacy.** For example:
           - "In the area of Speech and Language, the client demonstrated..."
           - "Regarding Literacy skills, the client showed progress in..."
        
        3. **Outline treatment interventions and outcomes:**
           - Describe therapy approaches used.
           - For each intervention or outcome, **specify whether it targeted Speech and Language or Literacy.**
           - Note effectiveness and client responsiveness.
           - When referring to sounds or phonemes, clearly distinguish them from letter names:
             - Use quotes for letter names (e.g., "a", "b").
             - Use slashes for phonemes (e.g., /a/, /b/).
        
        4. **Highlight areas of progress and ongoing challenges:**
           - Be specific about which goals were achieved, which showed partial progress (improvement but not yet mastered), and which remain areas of need.
           - **Clearly label the relevant domain (Speech and Language or Literacy) for each point.**
        
        5. **Provide clear, actionable recommendations and next treatment goals:**
           - For each goal that showed partial progress, state that it should remain a focus for treatment.
           - Include specific skill areas to target.
           - For each recommendation or goal, **indicate whether it applies to Speech and Language or Literacy.**
           - Suggest concrete next steps and strategies for upcoming sessions.
           - If applicable, recommend additional assessment, collaboration with caregivers, or home practice activities.
           - Do not use vague language (e.g., "continue as planned"). Be specific about which skills and strategies will be targeted.
        
        **Formatting:**
        - Use section headings:
          - "Clinical Observations & Trends"
          - "Interventions & Outcomes"
          - "Progress & Challenges"
          - "Recommendations & Next Treatment Goals"
        - Within each section, **clearly label the domain** of each item (Speech and Language or Literacy).
        - Write in full sentences using professional clinical language.
        - Keep the tone objective, precise, and focused on clinical relevance.
        
        **Important:**
        - Ignore any extraneous system data (e.g., "createdBy: [object Object]").
        - Do not include raw note text; instead, synthesize it into a cohesive narrative.
        - Where possible, reference dates or date ranges to illustrate progress over time.
        - Ensure clarity when describing phonemes versus letter names, using the specified notation.
        `
      }

      return await this.makeRequest('/chat', data, token);
    } catch (error) {
      logError('Failed to summarize SOAP notes', {
        clientName,
        error: error.message,
        soapNotesCount: soapNotes?.length || 0
      });
      throw new Error(`Failed to generate SOAP notes summary: ${error.message}`);
    }
  }

  async summarizeSOAPNotesByClientId(clientId, token) {
    try {
      logInfo('Starting SOAP notes summarization by client ID', {
        clientId,
        requestStartTime: new Date().toISOString()
      });

      // Define the system prompt here in aiService (centralizing AI logic)
      const systemPrompt = `You are an expert Speech-Language Pathologist (SLP) clinical documentation assistant.
      
      Your task is to review the provided array of SOAP notes for a single client and generate a professional, concise summary suitable for inclusion in clinical progress reports.
      
      **Instructions:**
      
      1. **Identify and summarize key clinical observations and trends over time**, including:
         - Client participation, engagement, and behavior.
         - Notable changes in skills (e.g., expressive/receptive language, articulation, phonological awareness, fluency, literacy).
         - Response to interventions.
      
      2. **Outline treatment interventions and outcomes:**
         - Describe therapy approaches used (e.g., morpheme segmentation, multisyllabic decoding, vocabulary development).
         - Note effectiveness and client responsiveness.
      
      3. **Highlight areas of progress and ongoing challenges:**
         - Be specific about which goals showed improvement.
         - Identify persistent difficulties or emerging needs.
      
      4. **Provide clear recommendations and next steps for treatment planning:**
         - Include suggestions for focus areas, strategies, or additional assessments if appropriate.
      
      **Formatting:**
      - Use clear section headings: "Clinical Observations & Trends," "Interventions & Outcomes," "Progress & Challenges," "Recommendations."
      - Write in full sentences, using professional clinical language.
      - Keep the tone objective, precise, and focused on clinical relevance.
      
      **Important:**
      - Ignore any extraneous system data (e.g., "createdBy: [object Object]").
      - Do not include raw note text; instead, synthesize it into a cohesive narrative.
      - Where possible, reference dates or date ranges to illustrate progress over time.`;

      // Use the existing API configuration utility
      const { baseURL, config } = generateConfig({ token });

      // Create an AbortController for timeout management
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minutes timeout

      try {
        logInfo('Starting backend SOAP summarization request', {
          url: `${baseURL}/api/ai/summarize-soap-notes/${clientId}`,
          clientId: clientId,
          systemPromptLength: systemPrompt.length,
          startTime: new Date().toISOString()
        });

        const response = await fetch(`${baseURL}/api/ai/summarize-soap-notes/${clientId}`, {
          method: 'POST',
          headers: {
            ...config.headers,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            system_prompt: systemPrompt
          }),
          signal: controller.signal,
        });

        const fetchEndTime = Date.now();
        clearTimeout(timeoutId);

        logInfo('Backend SOAP summarization request completed', {
          responseStatus: response.status,
          responseOk: response.ok,
          clientId: clientId
        });

        if (!response.ok) {
          const errorText = await response.text();
          logError('Backend returned non-OK response for SOAP summarization', {
            status: response.status,
            errorText: errorText,
            clientId: clientId
          });
          throw new Error(`AI Service Error: ${response.status} - ${errorText}`);
        }

        const result = await response.json();

        logInfo('SOAP notes summarization successful', {
          clientId: clientId,
          responseLength: JSON.stringify(result).length
        });

        return result;

      } catch (error) {
        clearTimeout(timeoutId);

        logError('Backend SOAP summarization request failed', {
          errorName: error.name,
          errorMessage: error.message,
          clientId: clientId,
          isAbortError: error.name === 'AbortError'
        });

        if (error.name === 'AbortError') {
          throw new Error(`AI processing timed out. The model may be processing a large request for this client.`);
        }
        throw error;
      }
    } catch (error) {
      logError('SOAP notes summarization by client ID failed', {
        clientId: clientId,
        error: error.message,
        stack: error.stack
      });

      // Provide more user-friendly error messages
      if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        throw new Error('Unable to connect to AI service. Please check your internet connection or contact support.');
      } else if (error.message.includes('CORS')) {
        throw new Error('Connection blocked by browser security. Please contact your system administrator.');
      } else if (error.message.includes('504') || error.message.includes('Gateway Time-out')) {
        throw new Error('Request timed out at the server level. The AI server may have processed your request successfully, but the response was cut off during transmission. Please try again, and if the issue persists, contact support.');
      } else if (error.message.includes('timed out')) {
        throw new Error('Request processing took longer than expected. Please try again or contact support if the issue persists.');
      } else if (error.message.includes('404')) {
        throw new Error('No completed SOAP notes found for this client, or client not found.');
      }

      throw error;
    }
  }

  formatSOAPNotesForAI(soapNotes, clientName) {
    if (!soapNotes || !Array.isArray(soapNotes) || soapNotes.length === 0) {
      return `No SOAP notes available for client: ${clientName}`;
    }

    let formattedText = `SOAP Notes Summary Request for Client: ${clientName}\n\n`;
    formattedText += `Total number of SOAP notes: ${soapNotes.length}\n\n`;

    soapNotes.forEach((note, index) => {
      formattedText += `--- SOAP Note ${index + 1} ---\n`;

      // Add service date if available
      if (note.serviceDate) {
        formattedText += `Service Date: ${new Date(note.serviceDate).toLocaleDateString()}\n`;
      }

      // Add appointment start date if serviceDate not available
      if (!note.serviceDate && note.start) {
        formattedText += `Service Date: ${new Date(note.start).toLocaleDateString()}\n`;
      }

      // Add appointment title if available
      if (note.title) {
        formattedText += `Session: ${note.title}\n`;
      }

      // Process the SOAP note content - handle both array and object structures
      if (note.soapNote) {
        let soapNoteData = null;

        // If soapNote is an array, get the latest note
        if (Array.isArray(note.soapNote) && note.soapNote.length > 0) {
          soapNoteData = note.soapNote[note.soapNote.length - 1];
        }
        // If soapNote is an object, use it directly
        else if (typeof note.soapNote === 'object' && note.soapNote !== null) {
          soapNoteData = note.soapNote;
        }

        if (soapNoteData) {
          // Handle different template types
          if (soapNoteData.templateUsed === 'structured') {
            const noteData = soapNoteData.noteData || {};

            if (noteData.subjective) {
              formattedText += `Subjective: ${this.stripHtml(noteData.subjective)}\n`;
            }
            if (noteData.objective) {
              formattedText += `Objective: ${this.stripHtml(noteData.objective)}\n`;
            }
            if (noteData.assessment) {
              formattedText += `Assessment: ${this.stripHtml(noteData.assessment)}\n`;
            }
            if (noteData.plan) {
              formattedText += `Plan: ${this.stripHtml(noteData.plan)}\n`;
            }

            // Add other structured fields if they exist
            Object.keys(noteData).forEach(key => {
              if (!['subjective', 'objective', 'assessment', 'plan'].includes(key) && noteData[key]) {
                // Handle both string values and arrays/objects
                if (typeof noteData[key] === 'string') {
                  formattedText += `${key}: ${this.stripHtml(noteData[key])}\n`;
                } else if (Array.isArray(noteData[key])) {
                  formattedText += `${key}: ${noteData[key].join(', ')}\n`;
                } else if (typeof noteData[key] === 'object') {
                  formattedText += `${key}: ${JSON.stringify(noteData[key])}\n`;
                } else {
                  formattedText += `${key}: ${noteData[key]}\n`;
                }
              }
            });

          } else if (soapNoteData.templateUsed === 'freeform') {
            const noteData = soapNoteData.noteData || {};

            Object.entries(noteData).forEach(([fieldName, fieldValue]) => {
              if (fieldValue) {
                if (typeof fieldValue === 'string') {
                  formattedText += `${fieldName}: ${this.stripHtml(fieldValue)}\n`;
                } else {
                  formattedText += `${fieldName}: ${fieldValue}\n`;
                }
              }
            });
          } else {
            // If no template type is specified, try to extract any available data
            const noteData = soapNoteData.noteData || soapNoteData;
            if (typeof noteData === 'object') {
              Object.entries(noteData).forEach(([fieldName, fieldValue]) => {
                if (fieldValue && typeof fieldValue === 'string') {
                  formattedText += `${fieldName}: ${this.stripHtml(fieldValue)}\n`;
                } else if (fieldValue) {
                  formattedText += `${fieldName}: ${fieldValue}\n`;
                }
              });
            }
          }
        }
      }

      formattedText += `\n`;
    });

    formattedText += `\nPlease provide a comprehensive summary of this client's treatment progress based on the above SOAP notes.`;

    return formattedText;
  }

  stripHtml(html) {
    if (!html) return '';
    // Basic HTML tag removal - in a real application, you might want to use a proper HTML parser
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  }
}

export default new AIService(); 